import React from "react";
import {
  ArrowRight,
  BarChart3,
  BrainCircuit,
  Check,
  ChevronDown,
  Compass,
  HeartHandshake,
  Lightbulb,
  Megaphone,
  MessageCircle,
  MousePointer2,
  Search,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import styles from "./CareerLanding.module.css";
import CareerTabs from "../Careers/CareerTabs";

const APPLY_URL = "https://recruit.ourdept.com/job-drive/aHJsYWJz/4";
const WHATSAPP_DISPLAY = "+91 86608 33699";
const WHATSAPP_LINK = "https://wa.me/918660833699";

const pageContent = {
  hr: {
    eyebrow: "HR Career Accelerator",
    label: "People, Purpose & Real-World Practice",
    title: (
      <>
        Don't just get an <em>HR title.</em>
        <br />
        Learn how work really works.
      </>
    ),
    intro:
      "A performance-led career launchpad for curious graduates who want to shape people, culture and growing businesses - from day one.",
    role: "HR",
    endRole: "HR Associate",
    graduateRole: "HR Executive / HR Generalist",
    accent: "hr",
    heroWords: ["People", "Culture", "Impact"],
    learnHeading: "You'll learn the HR they don't teach in textbooks.",
    learnIntro:
      "Go beyond recruitment and paperwork. Explore the people, systems and decisions behind great workplaces.",
    skills: [
      [
        UsersRound,
        "People experience",
        "Employee connect, engagement and clear communication.",
      ],
      [
        Compass,
        "HR operations",
        "Onboarding, policies, documentation and exits.",
      ],
      [
        BarChart3,
        "Performance",
        "KRAs, KPIs, reviews, feedback and development.",
      ],
      [
        BrainCircuit,
        "HR technology",
        "HRMS, dashboards, automation, analytics and AI.",
      ],
      [
        HeartHandshake,
        "Business HR",
        "Manager support, structure and real people problems.",
      ],
      [
        Search,
        "Practical knowledge",
        "Policy research, compliance and HR best practices.",
      ],
    ],
    audience: [
      "You notice how people feel at work.",
      "You ask thoughtful questions before jumping to answers.",
      "You can be trusted with details and follow-through.",
      "You want to solve people problems, not just fill positions.",
    ],
    proof:
      "A new teammate feels confused and disconnected after 30 days. What would you do?",
    challenge:
      "Apply using the link, then send your answer to us on WhatsApp in 100 words or fewer. No polished content needed - just show us how you think.",
    final: "Start building the HR career you want to grow into.",
  },
  marketing: {
    eyebrow: "Marketing Career Launchpad",
    label: "Ideas, experiments & measurable growth",
    title: (
      <>
        Don't just post.
        <em> Make people notice.</em>
      </>
    ),
    intro:
      "A performance-led career launchpad for curious graduates who want to turn sharp ideas into real campaigns, conversations and growth.",
    role: "Marketing",
    endRole: "Marketing Associate",
    graduateRole: "Marketing Executive / Specialist",
    accent: "marketing",
    heroWords: ["Ideas", "Experiments", "Growth"],
    learnHeading: "Your first marketing lab starts here.",
    learnIntro:
      "Learn by testing, measuring and improving - across the work that makes brands relevant and businesses grow.",
    skills: [
      [
        Lightbulb,
        "Content",
        "Ideas, copywriting, LinkedIn, social and creative content.",
      ],
      [
        Megaphone,
        "Growth",
        "Lead generation, outreach, campaigns and conversion.",
      ],
      [
        Search,
        "Research",
        "Markets, customers, competitors and emerging trends.",
      ],
      [
        MousePointer2,
        "Digital",
        "SEO, website content, email and digital campaigns.",
      ],
      [
        BrainCircuit,
        "Marketing tech",
        "AI, CRM, marketing tools and analytics.",
      ],
      [BarChart3, "Data", "Reach, engagement, leads and campaign performance."],
    ],
    audience: [
      "You see an idea and immediately wonder, 'would that work?'",
      "You enjoy writing, researching, testing and improving.",
      "You are comfortable hearing what didn't work - then trying again.",
      "You want to understand people, ideas and business together.",
    ],
    proof:
      "You have ₹0 budget and 30 days to get people talking about HR Labs. What would you try?",
    challenge:
      "Apply using the link, then send your answer to us on WhatsApp in 100 words or fewer. No jargon, no polished content - just let us hear your idea.",
    final: "Build something worth putting on your resume.",
  },
};

function ApplyButton({ onClick, kind = "primary" }) {
  return (
    <button
      className={`${styles.applyButton} ${kind === "light" ? styles.lightButton : ""}`}
      onClick={onClick}
    >
      Apply now <ArrowRight size={18} />
    </button>
  );
}

export default function CareerLanding({ type }) {
  const data = pageContent[type];
  const goToApplication = () =>
    document
      .getElementById("apply")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <>
      {/* HR and Marketing sit under Requirements, so that tab stays highlighted */}
      <CareerTabs activeTab="Requirement" />

      <main className={`${styles.page} ${styles[data.accent]}`}>
        <section className={styles.hero}>
          <div className={styles.noise} />
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}>
                <Sparkles size={40} /> {data.label}
              </p>
              <h1>{data.eyebrow}</h1>
              <h2 className={styles.heroTagline}>{data.title}</h2>
              <p className={styles.heroIntro}>{data.intro}</p>
              <div className={styles.heroActions}>
                <ApplyButton onClick={goToApplication} />
                <a href="#journey" className={styles.textLink}>
                  See the journey <ChevronDown size={18} />
                </a>
              </div>
              <p className={styles.noExperience}>
                <Check size={16} /> No experience needed. Your curiosity is your
                advantage.
              </p>
            </div>
            <div className={styles.heroVisual} aria-hidden="true">
              <div className={`${styles.orbit} ${styles.orbitOne}`} />
              <div className={`${styles.orbit} ${styles.orbitTwo}`} />
              <div className={styles.visualCard}>
                <span className={styles.visualIcon}>
                  {type === "hr" ? <UsersRound /> : <Megaphone />}
                </span>
                <p>{data.label}</p>
                <strong>
                  {type === "hr" ? "Human Resource Lab" : "Marketing Lab"}
                </strong>
              </div>
              <div className={styles.floatingTag}>{data.heroWords[0]}</div>
              <div className={`${styles.floatingTag} ${styles.tagTwo}`}>
                {data.heroWords[1]}
              </div>
              <div className={`${styles.floatingTag} ${styles.tagThree}`}>
                {data.heroWords[2]}
              </div>
            </div>
          </div>
        </section>

        <section id="journey" className={styles.journey}>
          <div className={styles.sectionIntro}>
            <p className={styles.kicker}>A Clear Path Forward</p>
            <h2>
              Your capability grows.
              <span> Your role follows.</span>
            </h2>
            <p>
              This isn't a title on day one. It is a focused, 14-month journey
              where every next step is earned.
            </p>
          </div>
          <div className={styles.timeline}>
            <article className={`${styles.stage} ${styles.internStage}`}>
              <span className={styles.stageNo}>01</span>
              <div className={styles.stageTop}>
                <span>Start</span>
              </div>
              <h3>{data.role} Intern - 3 Months</h3>

              <p>
                Learn the foundations, shadow the work and start contributing on
                real projects.
              </p>
              <b className={styles.unpaidBadge}>Unpaid internship</b>
            </article>
            <div className={styles.timelineArrow}>
              <ArrowRight />
            </div>
            <article className={`${styles.stage} ${styles.featuredStage}`}>
              <span className={styles.stageNo}>02</span>
              <div className={styles.stageTop}>
                <span>Grow</span>
              </div>
              <h3>{data.role} Trainee - 4 Months</h3>
              <p>
                Prove your momentum. Take ownership with close guidance from the
                team.
              </p>
              <b>
                ₹12,000-15,000 <small>/ month</small>
              </b>
            </article>
            <div className={styles.timelineArrow}>
              <ArrowRight />
            </div>
            <article className={styles.stage}>
              <span className={styles.stageNo}>03</span>
              <div className={styles.stageTop}>
                <span>Own</span>
              </div>
              <h3>{data.endRole} - 6 Months</h3>
              <p>
                Step into the real role, build depth and make your work count.
              </p>
              <b>
                {type === "hr" ? "₹20,000" : "₹20,000"} <small>/ month</small>
              </b>
            </article>
          </div>
          <div className={styles.progressNote}>
            <Sparkles size={18} />
            <span>
              <strong>Progression is performance-driven.</strong> Complete each
              stage to graduate to the next.
            </span>
          </div>
          <div className={styles.centerCta}>
            <ApplyButton onClick={goToApplication} />
          </div>
        </section>

        <section className={styles.learning}>
          <div className={styles.sectionIntro}>
            <p className={styles.kicker}>Real Work. Real Learning.</p>
            <h2>{data.learnHeading}</h2>
            <p>{data.learnIntro}</p>
          </div>
          <div className={styles.skillGrid}>
            {data.skills.map(([Icon, title, description]) => (
              <article className={styles.skillCard} key={title}>
                <span>
                  <Icon size={23} />
                </span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
          <div className={styles.centerCta}>
            <ApplyButton onClick={goToApplication} />
          </div>
        </section>

        <section className={styles.fitSection}>
          <div className={styles.fitPanel}>
            <p className={styles.kicker}>Who This Is For?</p>
            <h2>
              You don't need to know everything.{" "}
              <span>You need to want to find out.</span>
            </h2>
            <p className={styles.fitLead}>
              We are looking for early-career people who are ready to do more
              than collect a certificate.
            </p>
            <div className={styles.checkList}>
              {data.audience.map((item) => (
                <p key={item}>
                  <Check size={18} />
                  {item}
                </p>
              ))}
            </div>
            <ApplyButton onClick={goToApplication} />
          </div>
          <div className={styles.quotePanel}>
            <MessageCircle size={31} />
            <p>"I noticed something. Here's what I think we should try."</p>
            <small>That is the kind of thinking we love.</small>
            <div className={styles.quoteShape} />
          </div>
        </section>

        <section className={styles.why}>
          <div className={styles.whyHeader}>
            <p className={styles.kicker}>Why HR Labs?</p>
            <h2>Small enough to be seen. Real enough to matter.</h2>
          </div>
          <div className={styles.whyGrid}>
            <article>
              <strong>01</strong>
              <h3>Work on real problems</h3>
              <p>
                Get closer to the questions businesses are actually trying to
                solve - not made-up assignments.
              </p>
            </article>
            <article>
              <strong>02</strong>
              <h3>Learn in the doing</h3>
              <p>
                Observe, try, get feedback, improve. Your learning is active
                from your first week.
              </p>
            </article>
            <article>
              <strong>03</strong>
              <h3>Build proof, not just a CV</h3>
              <p>
                Leave with sharper judgement, practical stories and work you are
                proud to talk about.
              </p>
            </article>
          </div>
          <div className={styles.centerCta}>
            <ApplyButton onClick={goToApplication} />
          </div>
        </section>

        <section id="apply" className={styles.application}>
          <div className={styles.applicationInner}>
            <div>
              <p className={styles.kicker}>Your First Challenge</p>
              <h2>Before you apply, tell us how you think.</h2>
              <div className={styles.challenge}>
                <span>
                  <Lightbulb size={26} />
                </span>
                <p>{data.proof}</p>
              </div>
              <p className={styles.applicationNote}>{data.challenge}</p>
            </div>
            <div className={styles.applyCard}>
              <p>Ready to begin?</p>
              <h3>{data.final}</h3>
              <div className={styles.qrBox}>
                <QRCodeSVG
                  value={APPLY_URL}
                  size={180}
                  level="M"
                  marginSize={0}
                  fgColor="#111827"
                  bgColor="#ffffff"
                  title="Scan to open the application page"
                />
              </div>
              <p className={styles.qrCaption}>
                Scan above to apply or click below for application
              </p>
              <a
                className={styles.applicationButton}
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Start your application <ArrowRight size={18} />
              </a>
              <small className={styles.whatsappLine}>
                Send your answer on WhatsApp:{" "}
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {WHATSAPP_DISPLAY}
                </a>
              </small>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
